import * as React from 'react'
import Button from '@mui/material/Button'
import { AutocompleteChangeDetails, AutocompleteChangeReason, Box, Collapse, Dialog, DialogActions, DialogTitle, FormControlLabel, IconButton, Radio, RadioGroup, SelectChangeEvent, TextField, Tooltip, Typography } from '@mui/material'
import { appdata, defaultAMemberInfo, AMemberInfo, memberKindInfos, memberKind, generateMemberName } from '../internalModules'
import { boxStyle, dialogPaperProps, dialogTypographyStyle } from '../Graphics'
import UISelectMemberClass from './UISelectMemberClass'
import UISelectMaterial from './UISelectMaterial'
import UISelectShapeName from './UISelectShapeName'
import { getMemberShapeData, getMemberShapeInfo } from '../data/MemberShapeData'
import UILengthTextField from './UILengthTextField'
import { Dist } from '../Dist'
import UIMemberIcon from './UIMemberIcon'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import UIMemberDialogGraphics from './UIMemberDialogGraphics'

type Props = {
  memberGuid: string,
  isOpen: boolean,
  closeDialog: Function
  acceptDialog: Function
  deleteMember?: Function
}

export default function UIMemberDialog({memberGuid, isOpen, closeDialog, acceptDialog, deleteMember = ()=>{}}: Props){

  const mode = memberGuid.length > 0 ? 'edit' : 'add'
  let initialMemberInfo = getMemberShapeInfo('', defaultAMemberInfo.kind)
  let dialogTitle = 'Add New Member:'
  let initialShapeSource = 'list'

  if (mode === 'edit'){
    initialMemberInfo = appdata.getMember(memberGuid).info
    dialogTitle = 'Edit Member:'
    initialShapeSource = 'custom'
  }

  const [memberInfo, setMemberInfo] = React.useState(initialMemberInfo)
  const [shapeSource, setShapeSource] = React.useState(initialShapeSource)
  const [shapeName, setShapeName] = React.useState(initialMemberInfo.name)
  const [showHelp, setShowHelp] = React.useState(false)

  const toggleShowHelp = () => {
    setShowHelp(!showHelp)
  }

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const propertyName: string = (event.target as HTMLInputElement).name
    const value: string = (event.target as HTMLInputElement).value
    const newMemberInfo = { ...memberInfo, [propertyName]: value} as AMemberInfo
    setMemberInfo(newMemberInfo)
  }

  const handleChangeKind = (event: SelectChangeEvent) => {
    const newKind: string = (event.target as HTMLInputElement).value
    const newMemberInfo = getMemberShapeInfo('', newKind)
    setMemberInfo(newMemberInfo)
    setShapeName(newMemberInfo.name)
    // also update memberinfo for new selected member name
  }

  const handleChangeSource = (event: React.ChangeEvent<HTMLInputElement> ) => {
    let value: string = (event.target as HTMLInputElement).value
    if(value === 'custom'){
      setShapeName('') // clear shape name and member name
      setMemberInfo({...memberInfo, name: ''})
      setShapeSource(value)
      return
    }
    else{
      const newMemberInfo = getMemberShapeData(memberInfo.kind)[0]
      setShapeName(newMemberInfo.name)
      setMemberInfo(newMemberInfo)
      setShapeSource(value)
      return
    }
    
  }

  const handleChangeShapeName = (event: React.SyntheticEvent<Element, Event>, value: string | null, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<string> | undefined)  => {
    let newShapeName = value ? value : ''
    const newMemberInfo = getMemberShapeInfo(newShapeName, memberInfo.kind)
    setShapeName(newMemberInfo.name)
    setMemberInfo(newMemberInfo)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement> ) => {
    const propertyName: string = (event.target as HTMLInputElement).name
    const value: string = (event.target as HTMLInputElement).value
    const newMemberInfo = { ...memberInfo, [propertyName]: value} as AMemberInfo
    setMemberInfo(newMemberInfo)
  }

  const handleChangeLength = (propName: string, length: Dist) => {
    setMemberInfo({ ...memberInfo, [propName]: length.in})
  }

  const handleClickOk = () => {
    const info = memberInfo
    if(mode === 'edit'){
      info.guid = memberGuid
    }
    if(shapeSource === 'custom'){
      info.name = generateMemberName(info)
    }
    acceptDialog(memberInfo)
    closeDialog()
  }

  const handleClickCancel = () => {
    closeDialog()
  }

  function getProperties(kind: string): string[]{
    const mkind = memberKindInfos.find( x=> x.name === kind)
    if(mkind){
      return mkind.requiredInputs
    }
    else{
      return []
    }
  }

  function showDepthSettings(){
    const properties = getProperties(memberInfo.kind)
    let label = 'Depth:'
    if(memberInfo.kind.includes('Round')){
      label = 'Diameter:'
    }
    if(properties.includes('depth')){
      return (
        <UILengthTextField key={memberInfo.name + 'depth'} disabled={shapeSource === 'list'} propertyName='depth'
          inputPrefix={label} valueObject={memberInfo} onBlur={handleChangeLength} />
      )
    }
    else{return (<></>)}
  }

  function showWidthSettings(){
    const properties = getProperties(memberInfo.kind)
    if(properties.includes('width')){
      return (
        <UILengthTextField key={memberInfo.name + 'width'} disabled={shapeSource === 'list'} propertyName='width'
          inputPrefix='Width:' valueObject={memberInfo} onBlur={handleChangeLength} />
      )
    }
    else{return (<></>)}
  }

  function showThicknessSettings(){
    const properties = getProperties(memberInfo.kind)
    if(properties.includes('thickness')){
      return (
        <UILengthTextField key={memberInfo.name + 'thickness'}disabled={shapeSource === 'list'} propertyName='thickness'
          inputPrefix='Thickness:' valueObject={memberInfo} onBlur={handleChangeLength} />
      )
    }
    else{return (<></>)}
  }

  function showLipSettings(){
    const properties = getProperties(memberInfo.kind)
    if(properties.includes('lip')){
      return (
        <UILengthTextField key={memberInfo.name + 'lip'} disabled={shapeSource === 'list'} propertyName='lip'
          inputPrefix='Lip:' valueObject={memberInfo} onBlur={handleChangeLength} />
      )
    }
    else{return (<></>)}
  }

  function showWebFlangeSettings(){
    const properties = getProperties(memberInfo.kind)
    if(properties.includes('webThickness')){
      return (
        <>
          <UILengthTextField key={memberInfo.name + 'webThickness'} disabled={shapeSource === 'list'} propertyName='webThickness'
            inputPrefix='Web thickness:' valueObject={memberInfo} onBlur={handleChangeLength} />
          <UILengthTextField key={memberInfo.name + 'flangeThickness'}disabled={shapeSource === 'list'} propertyName='flangeThickness'
            inputPrefix='Flange thickness:' valueObject={memberInfo} onBlur={handleChangeLength} />
        </>
      )
    }
    else{return (<></>)}
  }

  function showMetalDeckSettings(){
    if(memberInfo.kind === memberKind.metalDeck){
      return (
        <>
          <UILengthTextField key={memberInfo.name + 'topFlute'} disabled={shapeSource === 'list'} propertyName='topFlute'
            inputPrefix='Top flute:' valueObject={memberInfo} onBlur={handleChangeLength} />
          <UILengthTextField key={memberInfo.name + 'bottomFlute'} disabled={shapeSource === 'list'} propertyName='bottomFlute'
            inputPrefix='Bottom flute:' valueObject={memberInfo} onBlur={handleChangeLength} />
        </>
      )
    }
    else{return (<></>)}
  }

  return(
    <Dialog
        open={isOpen}
        onClose={handleClickCancel}
        maxWidth='sm'
        fullWidth
        scroll='body'
        PaperProps={dialogPaperProps}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle id="alert-dialog-title" fontSize={30} sx={{pl:4}}>
          {dialogTitle}
        </DialogTitle>
        <Box sx={boxStyle}>
          {mode === 'add'?
          <UISelectMemberClass label='Member type:' kind={memberInfo.kind} handleChange={handleChangeKind}/>
          :<UIMemberIcon kind={memberInfo.kind} size={72}/>
          }
          <Typography sx={dialogTypographyStyle} color='secondary'>Shape:</Typography>
          <RadioGroup
            value={shapeSource}
            name="source"
            onChange={handleChangeSource}
            sx={{paddingLeft: 0, paddingBottom: 0}}
            row
          >
            <FormControlLabel value='list' control={<Radio />} label="Choose from list" />
            <FormControlLabel value='custom' control={<Radio />} label="Custom" />
          </RadioGroup>
          <UISelectShapeName shapeName={shapeName} customShape={shapeSource === 'custom'} memberClassFilter={memberInfo.kind} handleChange={handleChangeShapeName}/>
          <UISelectMaterial label='Material:' disabled={shapeSource === 'list'} hideGwpByArea propertyName='materialGuid' materialGuid={memberInfo.materialGuid} handleChange={handleChangeSelect} />

          {showDepthSettings()}
          {showWidthSettings()}
          {showThicknessSettings()}
          {showLipSettings()}
          {showWebFlangeSettings()}
          {showMetalDeckSettings()}
        </Box>
        <Box sx={boxStyle}>
          <Typography sx={dialogTypographyStyle} color='secondary'> Optional member notes:</Typography>
          <TextField multiline
            minRows={2}
            maxRows={10}
            style={{width: '100%'}}
            name='notes'
            value={memberInfo.notes}
            disabled={shapeSource === 'list'}
            onChange={handleChange}
            placeholder='enter optional notes...'
          />
        </Box>

        <DialogActions>
          <Tooltip arrow title='Toggle help diagram' placement='right'>
            <IconButton sx={{marginRight: '57%', marginBottom: 2}}  onClick={toggleShowHelp} disableRipple>
              <HelpOutlineOutlinedIcon color={showHelp? 'primary': 'disabled'}/>
            </IconButton>
          </Tooltip>
          <Button variant='contained' onClick={handleClickOk} sx={{marginBottom: 2}}>
            Ok
          </Button>
          <Button variant='outlined' onClick={handleClickCancel} sx={{marginRight: 3, marginBottom: 2}}>
            Cancel
          </Button>
        </DialogActions>
        <Collapse in={showHelp} timeout="auto" unmountOnExit>
          <UIMemberDialogGraphics kind={memberInfo.kind} />
        </Collapse>
    </Dialog>
  )
}